/* eslint-disable camelcase */

/**
 * @description List-scoped key. Matches getProspectListHeader's queryFn,
 * which destructures `{ queryKey: [, id] }` — id must sit at index 1.
 *
 * @param {number|string} listId
 * @return {['prospect-list', number|string]}
 */
export const prospectListKey = (listId) => ["prospect-list", listId];

/**
 * @description Partner-scoped key. Matches getProspectListItems' queryFn,
 * which destructures `{ queryKey: [, partnerId, listId, params] }`.
 *
 * The optional 4th element carries `include_excluded`. Callers that FETCH
 * must pass it so the two payload shapes never share a cache entry — a DC
 * (who sees tombstones) and a PM (who must not) would otherwise read each
 * other's cached rows. Callers that only INVALIDATE should omit it: that
 * yields a 3-element prefix key which matches every variant. Passing
 * `{ include_excluded: undefined }` would NOT prefix-match a cached
 * `{ include_excluded: true }` key, so the object must be omitted entirely
 * rather than defaulted — same trap as prospectListCommentsKey below.
 *
 * @param {number|string} partnerId
 * @param {number|string} listId
 * @param {{ include_excluded?: boolean }} [params]
 * @return {Array}
 */
export const prospectListItemsKey = (partnerId, listId, params) => {
  const base = ["prospect-list-items", partnerId, listId];

  return params ? [...base, { include_excluded: !!params.include_excluded }] : base;
};

/**
 * @description Partner-scoped key. Matches getProspectListComments' queryFn,
 * which destructures
 * `{ queryKey: [, partnerId, listId, { current_round_only }] }` with NO
 * default on the fourth element — it throws if that element is missing.
 * Callers that fetch (the read hook) must always pass `params` so the
 * fourth element is present. Callers that only invalidate (mutation hooks)
 * should omit `params` — that yields a 3-element prefix key, which matches
 * every comments query for the list regardless of its `current_round_only`
 * value. (Passing `{ current_round_only: undefined }` as a 4th element
 * would NOT prefix-match a cached `{ current_round_only: true }` key —
 * react-query's partial match compares value by value, and
 * `undefined !== true` — so the object must be omitted entirely rather than
 * defaulted.)
 *
 * @param {number|string} partnerId
 * @param {number|string} listId
 * @param {{ current_round_only?: boolean }} [params]
 * @return {['prospect-list-comments', number|string, number|string] | ['prospect-list-comments', number|string, number|string, { current_round_only?: boolean }]}
 */
export const prospectListCommentsKey = (partnerId, listId, params) => {
  const base = ["prospect-list-comments", partnerId, listId];

  return params ? [...base, { current_round_only: params.current_round_only }] : base;
};

/**
 * @description Mutation keys, NOT query keys — they are never used to read or
 * invalidate anything. `MutationCache.onError` in `queryClient.js` receives the
 * mutation object but has no other way to tell WHICH feature a failing mutation
 * belongs to (unlike `QueryCache.onError`, which is handed the queryKey), so
 * these exist purely as the mutation-side equivalent of a query key base: a
 * stable, greppable identifier the global error handler can match on. Kept in
 * this file, beside the query keys they pair with, so the two sets stay
 * visibly in sync.
 *
 * @return {['prospect-list-set-prospect-status']}
 */
export const setProspectStatusMutationKey = () => ["prospect-list-set-prospect-status"];

/**
 * @description See `setProspectStatusMutationKey`.
 *
 * @return {['prospect-list-update-list-status']}
 */
export const updateProspectListStatusMutationKey = () => ["prospect-list-update-list-status"];

/**
 * @description See `setProspectStatusMutationKey`.
 *
 * @return {['prospect-list-exclude-prospects']}
 */
export const excludeProspectsMutationKey = () => ["prospect-list-exclude-prospects"];
